import { useCallback, useEffect, useRef, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';

/**
 * Drop-in replacement for `useState` that persists the value under a stable key.
 * Keys should be namespaced per-calculator: e.g. `tabai.calc.motorBhp.v1`.
 *
 * Reads on mount are async; until the read resolves the returned value is the
 * `initial` value. Once hydrated, subsequent writes are debounced-written through.
 */
export function usePersistedState<T>(
  key: string,
  initial: T,
): [T, (next: T | ((prev: T) => T)) => void, { hydrated: boolean; clear: () => Promise<void> }] {
  const [value, setValueInner] = useState<T>(initial);
  const [hydrated, setHydrated] = useState(false);
  const keyRef = useRef(key);
  keyRef.current = key;

  // Hydrate
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = await AsyncStorage.getItem(key);
        if (cancelled) return;
        if (raw != null) {
          try {
            setValueInner(JSON.parse(raw) as T);
          } catch (e) {
            console.warn('[usePersistedState] JSON parse failed for key', key, ':', e);
            // fall back to initial; bad JSON
          }
        }
      } catch (e) {
        console.warn('[usePersistedState] hydrate failed for key', key, ':', e);
        // ignore read errors
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValueInner((prev) => {
        const resolved = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
        // fire-and-forget persist
        AsyncStorage.setItem(keyRef.current, JSON.stringify(resolved)).catch((e) =>
          console.warn('[usePersistedState] setItem failed for key', key, ':', e),
        );
        return resolved;
      });
    },
    [],
  );

  const clear = useCallback(async () => {
    try {
      await AsyncStorage.removeItem(keyRef.current);
    } catch (e) {
      console.warn('[usePersistedState] clear failed for key', keyRef.current, ':', e);
      // ignore
    }
    setValueInner(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [value, setValue, { hydrated, clear }];
}
